import {autoFinance,changeFinance} from '../lib/invoiceDefaults';
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../supabase';
import { useSettings } from '../useSettings';

const SERVICE_TYPES = ['CMP','CEMP','CDM','CLP','CMS','CTMP','CLMP','DCMP','DCMLP','SPA','CLP','EC','Transport Statement','Travel Plan','Road Design','Waste Management Plan','Air Quality','Other'];

const SERVICE_LABELS = {
  CMP: 'CMP — Construction Management Plan',
  CEMP: 'CEMP — Construction Environmental Management Plan',
  CDM: 'CDM — Construction Design & Management',
  CLP: 'CLP — Construction Logistics Plan',
  CMS: 'CMS — Construction Method Statement',
  CTMP: 'CTMP — Construction Traffic Management Plan',
  CLMP: 'CLMP — Construction Logistics & Management Plan',
  DCMP: 'DCMP — Demolition & Construction Management Plan',
  DCMLP: 'DCMLP — Demolition, Construction & Management Logistics Plan',
  SPA: 'SPA — Site Preliminary Assessment',
  EC: 'EC — Ecological Constraints',
};

const EMPTY_PROJECT = { customer_id: '', project_name: '', service_type: 'CMP', council: '', planning_ref: '', status: 'Active', order_date: '', delivery_date: '', notes: '' };
const EMPTY_FIN = {
  tender_fee: '', agreed_fee: '',invoice_amount_1:'',invoice_amount_2:'',
  invoice_no_1: '', invoice_date_1: '', invoice_due_date_1: '', paid_amount_1: '',
  invoice_no_2: '', invoice_date_2: '', invoice_due_date_2: '', paid_amount_2: '',
  inv2_schedule_months: 3,
  payment_notes: '',
};
const EMPTY_CUST = { full_name: '', company: '', type: 'Architect', email: '', phone: '' };

function defaultDelivery() {
  const d = new Date();
  d.setDate(d.getDate() + 14);
  return d.toISOString().split('T')[0];
}

export default function ProjectModal({ project, customers, onClose, onSaved, user }) {
  const canFinance=['owner','operations'].includes(user?.app_metadata?.role);
  const { serviceTypes, customerTypes } = useSettings();
  const [form, setForm] = useState({ ...EMPTY_PROJECT, delivery_date: defaultDelivery() });
  const [fin, setFin] = useState(EMPTY_FIN);
  const [newCust, setNewCust] = useState(EMPTY_CUST);
  const [createCust, setCreateCust] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [custSearch, setCustSearch] = useState('');
  const request = useRef(null);
  const automatic=useRef(autoFinance(project?.financials?.[0]||EMPTY_FIN));
  const [team,setTeam]=useState([]);
  useEffect(()=>{supabase.from('profiles').select('id,full_name').then(({data})=>setTeam(data||[]));},[]);

  useEffect(() => {
    if (project) {
      setForm(project);
      if (project.financials?.[0]) {
        const f = project.financials[0];
        automatic.current=autoFinance(f);
        setFin({
          tender_fee: f.tender_fee ?? '', agreed_fee: f.agreed_fee ?? '',
          invoice_amount_1:f.invoice_amount_1??'',invoice_amount_2:f.invoice_amount_2??'',
          invoice_no_1: f.invoice_no_1 ?? '', invoice_date_1: f.invoice_date_1 ?? '',
          invoice_due_date_1: f.invoice_due_date_1 ?? '',
          paid_amount_1: f.paid_amount_1 ?? '',
          invoice_no_2: f.invoice_no_2 ?? '', invoice_date_2: f.invoice_date_2 ?? '',
          invoice_due_date_2: f.invoice_due_date_2 ?? '',
          paid_amount_2: f.paid_amount_2 ?? '',
          inv2_schedule_months: f.inv2_schedule_months ?? 3,
          payment_notes: f.payment_notes ?? '',
        });
      }
    }
  }, [project]);

  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const setFn = (k, v) => {
    const result=changeFinance(fin,k,v,automatic.current,project?.financials?.[0]?.paid_amount);
    automatic.current=result.auto;setFin(result.fin);
  };
  const setNC = (k, v) => setNewCust(f => ({ ...f, [k]: v }));

  const filteredCustomers = customers.filter(c =>
    `${c.full_name} ${c.company}`.toLowerCase().includes(custSearch.toLowerCase())
  );

  const save = async () => {
    if (!form.project_name.trim()) { setError('Project name is required.'); return; }
    setSaving(true); setError('');
    try {
      const clean = object => Object.fromEntries(Object.entries(object).map(([k,v])=>[k,v===''?null:v]));
      const payload={p_id:project?.id||null,p_version:project?.version||null,
        p_project:clean(Object.fromEntries(['customer_id','project_name','service_type','council','planning_ref','status','order_date','delivery_date','notes','service_line','owner_id','drive_url'].map(k=>[k,form[k]??'']))),
        p_finance:{...clean(fin),version:project?.financials?.[0]?.version??null},p_customer:createCust?newCust:null};
      const signature=JSON.stringify(payload);
      if(request.current?.signature!==signature)request.current={signature,id:crypto.randomUUID()};
      if(!canFinance&&project)throw new Error('Existing projects can only be edited by operations.');
      const {owner_id,...operationalProject}=payload.p_project;
      const {data,error:e}=await supabase.rpc(canFinance?'save_project':'create_project_entry',canFinance?{...payload,p_request_id:request.current.id}:{p_project:operationalProject,p_customer:payload.p_customer,p_request_id:request.current.id});
      if(e||!data?.id)throw new Error(e?.message||'Save not confirmed');
      onSaved();
    } catch(e){setError(e.message||'Save failed; draft retained.');}
    setSaving(false);
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <h2>{project ? `Edit — ${project.project_code}` : 'New Project'}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {/* CUSTOMER */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
              <label style={{ margin: 0 }}>Customer</label>
              <button className="btn-ghost" style={{ fontSize: 12, padding: '4px 10px' }} onClick={() => setCreateCust(v => !v)}>
                {createCust ? '← Select existing' : '+ New customer'}
              </button>
            </div>
            {createCust ? (
              <div style={{ background: 'var(--bg3)', borderRadius: 'var(--radius)', padding: 14 }}>
                <div className="form-row cols-2">
                  <div><label>Full Name *</label><input value={newCust.full_name} onChange={e => setNC('full_name', e.target.value)} placeholder="Full name" /></div>
                  <div><label>Company</label><input value={newCust.company} onChange={e => setNC('company', e.target.value)} placeholder="Company name" /></div>
                </div>
                <div className="form-row cols-3">
                  <div><label>Type</label>
                    <select value={newCust.type} onChange={e => setNC('type', e.target.value)}>
                      {customerTypes.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </div>
                  <div><label>Email</label><input value={newCust.email} onChange={e => setNC('email', e.target.value)} placeholder="email@..." /></div>
                  <div><label>Phone</label><input value={newCust.phone} onChange={e => setNC('phone', e.target.value)} placeholder="07xxx..." /></div>
                </div>
              </div>
            ) : (
              <div>
                <div className="search-bar" style={{ marginBottom: 8 }}>
                  <span className="icon">⌕</span>
                  <input value={custSearch} onChange={e => setCustSearch(e.target.value)} placeholder="Search customers..." />
                </div>
                <select value={form.customer_id} onChange={e => setF('customer_id', e.target.value)} size={4} style={{ height: 'auto' }}>
                  <option value="">— No customer —</option>
                  {filteredCustomers.map(c => <option key={c.id} value={c.id}>{c.full_name}{c.company ? ` — ${c.company}` : ''}</option>)}
                </select>
              </div>
            )}
          </div>

          <hr className="divider" />

          {/* PROJECT DETAILS */}
          <div className="form-row cols-2"><label>Service line<select value={form.service_line||''} onChange={e=>setF('service_line',e.target.value)}><option value="">Not yet classified</option><option value="planning_reports">Planning Reports</option><option value="data_center_consultancy">Data Center Consultancy</option><option value="architectural_legacy">Architectural Design (legacy)</option></select></label>{canFinance&&<label>Project owner<select value={form.owner_id||''} onChange={e=>setF('owner_id',e.target.value)}><option value="">Unassigned</option>{team.map(p=><option key={p.id} value={p.id}>{p.full_name||'Team member'}</option>)}</select></label>}</div>
          <label>Drive folder / delivery link<input type="url" value={form.drive_url||''} onChange={e=>setF('drive_url',e.target.value)} placeholder="https://drive.google.com/…" /></label>

          <div className="form-row cols-2">
            <div><label>Project Name *</label><input value={form.project_name} onChange={e => setF('project_name', e.target.value)} placeholder="e.g. 4 Riverside, Knaresborough" /></div>
            <div>
              <label>Service Type</label>
              <select value={form.service_type} onChange={e => setF('service_type', e.target.value)}>
                {serviceTypes.map(s => <option key={s} value={s}>{SERVICE_LABELS[s] ? `${s} — ${SERVICE_LABELS[s]}` : s}</option>)}
              </select>
            </div>
          </div>
          <div className="form-row cols-3">
            <div><label>Council</label><input value={form.council} onChange={e => setF('council', e.target.value)} placeholder="e.g. Westminster" /></div>
            <div><label>Planning Ref</label><input value={form.planning_ref} onChange={e => setF('planning_ref', e.target.value)} placeholder="e.g. 22/1512/GPD" /></div>
            <div><label>Status</label>
              <select value={form.status} onChange={e => setF('status', e.target.value)}>
                <option value="Active">Active</option>
                <option value="Pending">Pending</option>
                <option value="Completed">Completed</option>
                <option value="Rejected">Rejected</option>
                <option value="Disputed">Disputed</option>
              </select>
            </div>
          </div>
          <div className="form-row cols-2">
            <div><label>Order Date</label><input type="date" value={form.order_date} onChange={e => setF('order_date', e.target.value)} /></div>
            <div><label>Delivery Date</label><input type="date" value={form.delivery_date} onChange={e => setF('delivery_date', e.target.value)} /></div>
          </div>
          <div className="form-row"><div><label>Notes</label><textarea rows={2} value={form.notes} onChange={e => setF('notes', e.target.value)} placeholder="Project notes..." /></div></div>

          <hr className="divider" />

          {canFinance&&<>
          {/* FINANCIALS */}
          <label style={{ fontSize: 12, color: 'var(--accent)', display: 'block', marginBottom: 12 }}>FINANCIALS</label>
          <div className="form-row cols-2">
            <div><label>Tender Fee (£)</label><input type="number" value={fin.tender_fee} onChange={e => setFn('tender_fee', e.target.value)} placeholder="0.00" /></div>
            <div><label>Agreed Fee (£)</label><input type="number" value={fin.agreed_fee} onChange={e => setFn('agreed_fee', e.target.value)} placeholder="0.00" /></div>
          </div>
          <p className="ops-hint">Tender fills agreed fee; invoice totals default to 50/50. Manual amounts and issued/paid invoices are preserved. Amounts do not issue invoices.</p>
          <div className="form-row cols-2"><label>Invoice 1 total (£)<input type="number" min="0" step="0.01" value={fin.invoice_amount_1??''} onChange={e=>setFn('invoice_amount_1',e.target.value)} /></label><label>Invoice 2 total (£)<input type="number" min="0" step="0.01" value={fin.invoice_amount_2??''} onChange={e=>setFn('invoice_amount_2',e.target.value)} /></label></div>
          {/* Invoice 1 */}
          <div className="form-row cols-2" style={{ marginBottom: 6 }}>
            <div><label>Invoice No. 1</label><input value={fin.invoice_no_1} onChange={e => setFn('invoice_no_1', e.target.value)} placeholder="INV-001" /></div>
            <div><label>Invoice Date 1</label><input type="date" value={fin.invoice_date_1} onChange={e => setFn('invoice_date_1', e.target.value)} /></div>
          </div>
          <div className="form-row cols-2" style={{ marginBottom: 14 }}>
            <div><label>Due Date 1 (auto = same day)</label><input type="date" value={fin.invoice_due_date_1} onChange={e => setFn('invoice_due_date_1', e.target.value)} /></div>
            <div><label>Paid for Inv 1 (£)</label><input type="number" value={fin.paid_amount_1} onChange={e => setFn('paid_amount_1', e.target.value)} placeholder="0.00" /></div>
          </div>

          {/* Invoice 2 */}
          <div className="form-row cols-2" style={{ marginBottom: 6 }}>
            <div>
              <label>Invoice 2 schedule</label>
              <select value={fin.inv2_schedule_months} onChange={e => setFn('inv2_schedule_months', parseInt(e.target.value, 10))}>
                <option value={3}>3 months after Inv 1 (default)</option>
                <option value={1}>1 month after Inv 1</option>
              </select>
            </div>
            <div />
          </div>
          <div className="form-row cols-2" style={{ marginBottom: 6 }}>
            <div><label>Invoice No. 2</label><input value={fin.invoice_no_2} onChange={e => setFn('invoice_no_2', e.target.value)} placeholder="INV-002" /></div>
            <div><label>Invoice Date 2</label><input type="date" value={fin.invoice_date_2} onChange={e => setFn('invoice_date_2', e.target.value)} /></div>
          </div>
          <div className="form-row cols-2">
            <div><label>Due Date 2 (auto = same day)</label><input type="date" value={fin.invoice_due_date_2} onChange={e => setFn('invoice_due_date_2', e.target.value)} /></div>
            <div><label>Paid for Inv 2 (£)</label><input type="number" value={fin.paid_amount_2} onChange={e => setFn('paid_amount_2', e.target.value)} placeholder="0.00" /></div>
          </div>

          <div className="form-row"><div><label>Payment Notes</label><textarea rows={2} value={fin.payment_notes} onChange={e => setFn('payment_notes', e.target.value)} placeholder="e.g. Invoice sent 15 May, chasing..." /></div></div>

          </>}
          {error && <p style={{ color: 'var(--red)', fontSize: 13, marginTop: 4 }}>{error}</p>}
        </div>
        <div className="modal-footer">
          <button className="btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn-primary" onClick={save} disabled={saving}>{saving ? 'Saving...' : project ? 'Save Changes' : 'Create Project'}</button>
        </div>
      </div>
    </div>
  );
}
